import { useEffect, useRef, useState } from 'react';
import { groupsApi } from '../../../services/groups-api-client';
import { approximateSms, estimateFromServer, type SmsEstimate } from '../../../lib/smsEstimate';

/** How long the host stops typing before the server is asked (the API composes and measures the text). */
export const SMS_ESTIMATE_DEBOUNCE_MS = 400;

/** An API that predates `sms-estimate` answers 405/501 for every trip: not asked again this session. */
const MISSING_EVERYWHERE = new Set([405, 501]);
/**
 * 403 (a co-host: the endpoint is owner-only) and 404 (a stranger, or no such route) hold for THIS trip only: the owner of
 * another trip, in the same session, is a different answer. Remembered per trip.
 */
const MISSING_FOR_TRIP = new Set([403, 404]);
let missingEverywhere = false;
const missingForTrip = new Set<string>();
/** Tests only: forget what a previous test learned about the API. */
export function resetSmsEstimateSupport() {
  missingEverywhere = false;
  missingForTrip.clear();
}

const DEFAULT_RETRY_AFTER_S = 5;
const MAX_RETRY_AFTER_S = 60;

/** Seconds to wait after a 429: its Retry-After header when it is a number, else a short default, never more than a minute. */
function retryAfterSeconds(error: unknown): number {
  const headers = (error as { response?: { headers?: Record<string, unknown> } })?.response?.headers;
  const raw = headers ? (headers['retry-after'] ?? headers['Retry-After']) : undefined;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, MAX_RETRY_AFTER_S) : DEFAULT_RETRY_AFTER_S;
}

export interface SmsEstimateItem {
  /** Whatever the caller calls this text (a plan step's key, or 'composer'). */
  id: string;
  /** The text exactly as it will be sent; blank means there is nothing to count. */
  message: string;
}

export interface SmsEstimateTrip {
  title: string;
  slug: string;
  location?: string | null;
}

/** What an answer depends on: the text, the link choice, and the trip it is wrapped in (title, slug and location all reach the composed text). */
const keyOf = (message: string, personalized: boolean, trip: SmsEstimateTrip | null | undefined) => (
  [personalized ? 1 : 0, trip?.title ?? '', trip?.slug ?? '', trip?.location ?? '', message].join('\u0000')
);

/**
 * The server's count of each text, with a clearly approximate local one standing in while it is on its way (or
 * if the API cannot be asked). The server is asked once per distinct text, 400 ms after the host stops typing;
 * `estimateFor` returns the server's answer for a text it has answered, otherwise the local worst case
 * (`source: 'approx'`), which the UI labels "about" and which never blocks anything.
 *
 * What is remembered, and what is not: a 400 (that exact body is refused) is remembered. A 429 is asked again after its
 * Retry-After. A 5xx or a network failure is NOT remembered, so the next edit asks again. 401 changes nothing (the host may sign
 * back in). 403/404 stop the asking for this trip, and 405/501 for every trip.
 */
export function useSmsEstimates({
  groupId,
  items,
  personalize,
  trip,
  enabled = true,
}: {
  groupId: string;
  items: readonly SmsEstimateItem[];
  personalize: boolean;
  trip: SmsEstimateTrip | null | undefined;
  enabled?: boolean;
}) {
  // null = this exact text is refused (400): do not ask again for it.
  const answers = useRef(new Map<string, SmsEstimate | null>());
  const asking = useRef(new Set<string>());
  const alive = useRef(true);
  const [retryNonce, setRetryNonce] = useState(0);
  const [, rerender] = useState(0);

  // 429 retries wait on timers; they die with the component rather than firing into an unmounted one.
  const retryTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    alive.current = true;
    const timers = retryTimers.current;
    return () => {
      alive.current = false;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const wanted = items
    .filter((item) => item.message.trim())
    .map((item) => ({ key: keyOf(item.message, personalize, trip), message: item.message }));
  const signature = JSON.stringify(wanted.map((entry) => entry.key));

  useEffect(() => {
    if (!enabled || missingEverywhere || missingForTrip.has(groupId)) return undefined;
    const todo = new Map<string, string>();
    for (const entry of wanted) {
      if (!answers.current.has(entry.key) && !asking.current.has(entry.key)) todo.set(entry.key, entry.message);
    }
    if (todo.size === 0) return undefined;
    const timer = setTimeout(() => {
      todo.forEach((message, key) => {
        asking.current.add(key);
        Promise.resolve()
          .then(() => groupsApi.estimateSms(groupId, { smsBody: message, personalizeInviteLinks: personalize }))
          .then((response) => {
            const estimate = estimateFromServer(response);
            if (estimate) answers.current.set(key, estimate);
          })
          .catch((error: unknown) => {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 400) answers.current.set(key, null);
            else if (status !== undefined && MISSING_EVERYWHERE.has(status)) missingEverywhere = true;
            else if (status !== undefined && MISSING_FOR_TRIP.has(status)) missingForTrip.add(groupId);
            else if (status === 429) {
              const wait = setTimeout(() => {
                retryTimers.current.delete(wait);
                if (alive.current) setRetryNonce((n) => n + 1);
              }, retryAfterSeconds(error) * 1000);
              retryTimers.current.add(wait);
            }
            // 401, 5xx, a network failure: nothing remembered, the next edit asks again.
          })
          .finally(() => {
            asking.current.delete(key);
            if (alive.current) rerender((n) => n + 1);
          });
      });
    }, SMS_ESTIMATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  // `signature` is the text of every item and the trip it is wrapped in; the rest are stable per trip.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, groupId, personalize, enabled, retryNonce]);

  /** The count for one text: the server's if it has answered, else the local approximation; null when there is no text. */
  const estimateFor = (id: string): SmsEstimate | null => {
    const item = items.find((candidate) => candidate.id === id);
    if (!item || !item.message.trim() || !trip) return null;
    const answered = answers.current.get(keyOf(item.message, personalize, trip));
    return answered ?? approximateSms({
      title: trip.title,
      message: item.message,
      slug: trip.slug,
      location: trip.location,
      personalized: personalize,
    });
  };

  return { estimateFor };
}
